import { useState, useEffect } from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { X } from "lucide-react";
import { format, setYear } from "date-fns";
import { cn } from "@/lib/utils";
import { OCCASION_TYPES, OCCASION_TYPE_KEYS, REPEATABLE_OCCASIONS, isYearRequired, type OccasionType } from "@/lib/occasion-types";
import DatePickerField from "@/components/ui/date-picker-field";
import { useTierGate } from "@/hooks/useTierGate";
import { PaywallModal } from "@/components/PaywallModal";
import PlusBadge from "@/components/PlusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface OccasionFormData {
  occasionType: OccasionType;
  date: string; // YYYY-MM-DD
  label: string | null;
  isRecurring: boolean;
  babyName?: string;
  reminderLeadTime?: string | null;
}

interface AddOccasionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: OccasionFormData) => void;
  existingOccasionTypes: OccasionType[];
  editingOccasion?: OccasionFormData | null;
}

const LEAD_TIME_OPTIONS = [
  { value: "same_day", label: "Same day" },
  { value: "1_day", label: "1 day" },
  { value: "3_days", label: "3 days" },
  { value: "1_week", label: "1 week" },
  { value: "2_weeks", label: "2 weeks" },
  { value: "1_month", label: "1 month" },
] as const;

const LEAD_TIME_DISPLAY: Record<string, string> = {
  same_day: "same day",
  "1_day": "1 day",
  "3_days": "3 days",
  "1_week": "1 week",
  "2_weeks": "2 weeks",
  "1_month": "1 month",
};

const AddOccasionSheet = ({ open, onOpenChange, onSave, existingOccasionTypes, editingOccasion }: AddOccasionSheetProps) => {
  const { canUseOccasionType, isPlus, canAccess } = useTierGate();
  const { user } = useAuth();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallTitle, setPaywallTitle] = useState("");
  const [paywallBenefit, setPaywallBenefit] = useState("");
  const [selectedType, setSelectedType] = useState<OccasionType | null>(null);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [yearPicked, setYearPicked] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [isRecurring, setIsRecurring] = useState(true);
  const [babyName, setBabyName] = useState("");
  const [reminderLeadTime, setReminderLeadTime] = useState<string | null>(null);
  const [globalLeadTime, setGlobalLeadTime] = useState<string>("3_days");

  // Fetch user's global lead time
  useEffect(() => {
    if (!user || !open) return;
    supabase
      .from("profiles")
      .select("reminder_lead_time")
      .eq("user_id", user.id)
      .single()
      .then(({ data }) => {
        if (data?.reminder_lead_time) {
          setGlobalLeadTime(data.reminder_lead_time);
        }
      });
  }, [user, open]);

  // Sync state when opening with editing data
  useEffect(() => {
    if (open && editingOccasion) {
      setSelectedType(editingOccasion.occasionType);
      setDate(new Date(editingOccasion.date + "T00:00:00"));
      setCustomLabel(editingOccasion.label ?? "");
      setIsRecurring(editingOccasion.isRecurring);
      setBabyName(editingOccasion.babyName ?? "");
      setReminderLeadTime(editingOccasion.reminderLeadTime ?? null);
      const yr = new Date(editingOccasion.date + "T00:00:00").getFullYear();
      setYearPicked(yr !== 2000);
    } else if (open && !editingOccasion) {
      resetForm();
    }
  }, [open, editingOccasion]);

  const isEditing = !!editingOccasion;

  const resetForm = () => {
    setSelectedType(null);
    setDate(undefined);
    setYearPicked(false);
    setCustomLabel("");
    setIsRecurring(true);
    setBabyName("");
    setReminderLeadTime(null);
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) resetForm();
    onOpenChange(v);
  };

  const yearRequired = selectedType ? isYearRequired(selectedType, isRecurring) : false;
  const yearOptional = selectedType ? !yearRequired : false;

  const canSave = !!selectedType && !!date && (
    selectedType !== "special_date" || customLabel.trim().length > 0
  ) && (!yearRequired || yearPicked);

  const handleSave = () => {
    if (!selectedType || !date) return;

    const dateStr = yearRequired || yearPicked
      ? format(date, "yyyy-MM-dd")
      : format(setYear(date, 2000), "yyyy-MM-dd");

    onSave({
      occasionType: selectedType,
      date: dateStr,
      label: selectedType === "special_date" ? customLabel.trim() : null,
      isRecurring: selectedType === "special_date" ? isRecurring : (OCCASION_TYPES[selectedType].recurring ?? true),
      babyName: selectedType === "new_baby" ? babyName.trim() : undefined,
      reminderLeadTime,
    });

    resetForm();
    onOpenChange(false);
  };

  const typesToShow = OCCASION_TYPE_KEYS.filter(
    (t) => !existingOccasionTypes.includes(t) || REPEATABLE_OCCASIONS.has(t) || (isEditing && editingOccasion?.occasionType === t)
  );

  const handleTypeClick = (type: OccasionType) => {
    if (!canUseOccasionType(type)) {
      setPaywallTitle("Multiple occasion types is a Circle Plus feature");
      setPaywallBenefit("Track anniversaries, new babies, graduations, bereavements and more — every important moment, remembered.");
      setPaywallOpen(true);
      return;
    }
    setSelectedType(type);
  };

  const canUseCustomLeadTime = canAccess("customLeadTimePerOccasion");

  const handleLeadTimeClick = (value: string) => {
    if (!canUseCustomLeadTime) {
      setPaywallTitle("Custom reminder timing is a Circle Plus feature");
      setPaywallBenefit("Set unique reminder lead times for each occasion — never miss what matters most.");
      setPaywallOpen(true);
      return;
    }
    setReminderLeadTime(value);
  };

  const globalLeadTimeDisplay = LEAD_TIME_DISPLAY[globalLeadTime] || "3 days";

  return (
    <>
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerContent className="max-h-[90vh]">
          <div className="mx-auto w-full max-w-md overflow-y-auto px-6 pb-8">
            <DrawerHeader className="flex items-center justify-between px-0">
              <DrawerTitle className="font-serif text-xl font-bold">
                {isEditing ? "Edit occasion" : "Add an occasion"}
              </DrawerTitle>
              <button onClick={() => handleOpenChange(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </DrawerHeader>

            {/* Step 1: Occasion type selector */}
            <div className="mb-5">
              <Label className="mb-2 block text-sm font-bold text-foreground">What's the occasion?</Label>
              <div className="space-y-2">
                {typesToShow.map((type) => {
                  const config = OCCASION_TYPES[type];
                  const isSelected = selectedType === type;
                  const isLocked = !canUseOccasionType(type);
                  return (
                    <button
                      key={type}
                      onClick={() => handleTypeClick(type)}
                      className={cn(
                        "flex w-full items-center justify-between rounded-2xl border p-3 text-left transition-colors",
                        isSelected
                          ? "border-l-[3px] border-l-accent bg-accent/5 border-t-border border-r-border border-b-border"
                          : "border-border bg-card hover:border-accent/50"
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-base">{config.emoji}</span>
                        <span className="text-sm font-medium text-foreground">{config.label}</span>
                        {isLocked && <PlusBadge />}
                      </span>
                      {!isLocked && (
                        <span
                          className={cn(
                            "h-4 w-4 rounded-full border-2 transition-colors",
                            isSelected ? "border-accent bg-accent" : "border-muted-foreground/30"
                          )}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Date picker */}
            {selectedType && (
              <div className="mb-5">
                <Label className="mb-2 block text-sm font-bold text-foreground">When is it?</Label>
                <DatePickerField
                  value={date}
                  onChange={(d, yrPicked) => {
                    setDate(d);
                    setYearPicked(yrPicked);
                  }}
                  placeholder="Select a date"
                  title="When is it?"
                  yearOptional={yearOptional}
                  yearRequired={yearRequired}
                />
              </div>
            )}

            {/* Step 3: Conditional fields */}
            {selectedType === "special_date" && date && (
              <div className="mb-5 space-y-4">
                <div>
                  <Label className="mb-2 block text-sm font-bold text-foreground">Give it a name</Label>
                  <Input
                    placeholder="e.g. The day we met"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    maxLength={50}
                    className="rounded-full"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-foreground">Repeats every year</Label>
                  <Switch checked={isRecurring} onCheckedChange={setIsRecurring} />
                </div>
              </div>
            )}

            {selectedType === "new_baby" && date && (
              <div className="mb-5">
                <Label className="mb-2 block text-sm font-bold text-foreground">Baby's name (optional)</Label>
                <Input
                  placeholder="e.g. Emma"
                  value={babyName}
                  onChange={(e) => setBabyName(e.target.value)}
                  maxLength={50}
                  className="rounded-full"
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  We'll remind you to add them to your Circle on their first birthday 💛
                </p>
              </div>
            )}

            {/* Step 4: Reminder lead time */}
            {selectedType && date && (
              <div className="mb-5">
                <div className="mb-2 flex items-center gap-1">
                  <Label className="text-sm font-bold text-foreground">Reminder lead time</Label>
                  {!canUseCustomLeadTime && <PlusBadge />}
                </div>

                {canUseCustomLeadTime ? (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {LEAD_TIME_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => handleLeadTimeClick(opt.value)}
                          className={cn(
                            "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                            reminderLeadTime === opt.value
                              ? "border-accent bg-accent/10 text-accent"
                              : "border-border bg-card text-foreground hover:border-accent/50"
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {reminderLeadTime ? (
                      <button
                        onClick={() => setReminderLeadTime(null)}
                        className="mt-2 text-xs text-accent hover:underline"
                      >
                        Reset to default
                      </button>
                    ) : (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Using your default ({globalLeadTimeDisplay})
                      </p>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => handleLeadTimeClick("")}
                    className="w-full rounded-2xl border border-border bg-card p-3 text-left"
                  >
                    <p className="text-xs text-muted-foreground">
                      Upgrade to Circle Plus to set custom reminders
                    </p>
                  </button>
                )}
              </div>
            )}

            {/* Save button */}
            <Button
              onClick={handleSave}
              disabled={!canSave}
              className="w-full rounded-full"
              size="lg"
            >
              Save occasion
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        title={paywallTitle}
        benefit={paywallBenefit}
      />
    </>
  );
};

export default AddOccasionSheet;
